import { Localized, useLocalization } from "@fluent/react"
import { useAppDispatch, useAppSelector, useDataGridCustomLocaleText } from "../../../app/hooks"
import { useCallback, useEffect, useState } from "react"
import { Box, Button, ButtonGroup, Paper, TableContainer, Typography } from "@mui/material"
import { FlexDiv } from "../../../app/Utils"
import { DataGrid, GridColDef, GridRowId, gridExpandedSortedRowIdsSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid"
import { loadProjects, ProjectHeader, selectAllProjects, selectProjects } from "../projectSlice"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { useNavigate } from "react-router-dom"
import { selectSettings } from "../../settings/settingsSlice"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { ProcessFilterPanel } from "../Components/ProcessFilterPanel"
import { defProjectFilters, filtersToQueryString } from "../helpers/helper"
import { ProjectListFilters } from "../../documents/utils/types"
import { useLocalStorage } from "../../../useLocalStorage"
import { AddFabButton } from "../../../app/components/AddFabButton"
import { APIError } from "../../../app/appTypes"
import { createValidationProject, loadValidationProjects } from "./validationProjectSlice"
import { loadValidationProcesses } from "../validationProcess/validationProcessApi"
import { unwrapResult } from "@reduxjs/toolkit"
import { loadProcesses } from "../../process/processSlice"
import ValidationProjectWizard from "./dialogs/ValidationProjectWizard"
import { entityRoutes } from "../../../app/components/EntityBreadcrumbs"
import { ValidationProjectStatusPill } from "./components/ValidationProjectStatusPill"
import { LpField } from "../../../app/components/LpField"
import { Chip } from "@mui/material"
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined"

const VisualRowNumberCell = ({ id }: { id: GridRowId }) => {
    const apiRef = useGridApiContext()
    const visibleRowIds = useGridSelector(apiRef, gridExpandedSortedRowIdsSelector)
    const rowNumber = visibleRowIds.indexOf(id) + 1

    return (
        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LpField value={rowNumber} />
        </Box>
    )
}

export const ValidationProjectsList = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
    const [projects, setProjects] = useState<ProjectHeader[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const localeText = useDataGridCustomLocaleText();
    const [openWizard, setOpenWizard] = useState(false);


    const loadProjects = useCallback(() => {
        setIsLoading(true);
        setIsError(false);
        setError(null);
        dispatch(loadValidationProjects({}))
            .then(unwrapResult)
            .then(setProjects)
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => {
                setIsLoading(false)
                setHasLoadedOnce(true)
            });
    }, [dispatch])



    useEffect(() => {
        loadProjects()
    }, [loadProjects])



    const handleGoToClick = (id: GridRowId) => {
        navigate(entityRoutes.project(String(id), undefined, "list"))
    };

    const handleOpenCreateForm = useCallback(() => {
        setOpenWizard(true)
    }, [])

    const handleCloseWizard = useCallback(() => {
        setOpenWizard(false)
    }, [])

    const getCreateErrorMessage = useCallback((error: APIError | undefined) => {
        if (error?.kind === "http") {
            const errorKey = error.problem?.title
            if (errorKey) {
                return l10n.getString(errorKey, null, errorKey)
            }

            return l10n.getString("error", null, "error")
        }

        return l10n.getString("error", null, "error")
    }, [l10n])


    const columns: GridColDef[] =
        [
            {
                field: "lp",
                headerName: "Lp",
                width: 80,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                disableColumnMenu: true,
                renderCell: (params) => <VisualRowNumberCell id={params.id} />,
            },
            {
                field: 'no',
                width: 200,
                headerName: l10n.getString('project-no', null, 'Numer projektu'),

            },
            {
                field: 'name',
                headerName: l10n.getString('project-title', null, 'Tytuł projektu'),
                flex: 1
            },
            {
                field: 'companyName',
                headerName: l10n.getString(
                    'project-company-name',
                    null,
                    'Nazwa firmy'
                ),
                width: 400,

                renderCell: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            height: "100%"
                        }}
                    >
                        <Typography variant="body2">
                            {params.row.companyName}
                        </Typography>

                        {params.row.isExternal && (
                            <Chip
                                icon={<PublicOutlinedIcon />}
                                label={l10n.getString(
                                    "project-external",
                                    null,
                                    "Zewnętrzny"
                                )}
                                size="small"
                                variant="outlined"
                                color="info"
                            />
                        )}
                    </Box>
                )
            },
            {
                field: 'createDate',
                type: 'date',
                headerName: l10n.getString('project-create-date', null, 'Data utworzenia'),
                width: 150,
                valueGetter: (_, row) => new Date(row.createDate),
            },
            {
                field: 'status',
                headerName: l10n.getString('project-status', null, 'Status'),
                width: 150,
                renderCell: (params) => (
                    <div>
                        <ValidationProjectStatusPill status={params.value} />
                    </div>

                )
            },
            {
                field: 'actions', type: 'actions', width: 200,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button onClick={() => handleGoToClick(params.id)} color="secondary" variant="outlined">
                                <Localized id="go-to">
                                    <span>Przejdź</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ];

    if (error) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading && !hasLoadedOnce) {
        return <>
            <LoadingContainer />
        </>
    }

    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <div style={{ display: 'flex', height: '100%' }}>

                    <DataGrid
                        localeText={localeText}
                        rows={projects}
                        columns={columns}
                        style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                        slots={{ toolbar: QuickSearchToolbar }}
                        onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                        initialState={{
                            sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                        }}
                    />
                </div>

                <FlexDiv>
                    <FlexDiv>
                        <AddFabButton
                            onClick={handleOpenCreateForm}
                        />
                    </FlexDiv>
                </FlexDiv>
            </TableContainer>

            <ValidationProjectWizard
                open={openWizard}
                onClose={handleCloseWizard}
                onSave={async (payload) => {
                    const result = await dispatch(createValidationProject(payload))

                    if (createValidationProject.fulfilled.match(result)) {
                        const createdProject = result.payload
                        setOpenWizard(false)
                        loadProjects()
                        navigate(entityRoutes.project(String(createdProject.id), undefined, "list"))
                        return
                    }

                    const errorMsg = getCreateErrorMessage(result.payload)
                    throw new Error(errorMsg)
                }}
            />

        </>
    )
}
